import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { AlertTriangle, Clock, QrCode, UserX, Copy, CheckCheck, Bell } from "lucide-react";

const TYPE_META = {
  missing_return: { icon: Clock, label: "Missing Return", color: "text-red-600 bg-red-50 border-red-200" },
  late_return: { icon: Clock, label: "Late Return", color: "text-orange-600 bg-orange-50 border-orange-200" },
  wrong_provider: { icon: UserX, label: "Wrong Provider", color: "text-purple-600 bg-purple-50 border-purple-200" },
  duplicate_pickup: { icon: Copy, label: "Duplicate Pickup", color: "text-amber-600 bg-amber-50 border-amber-200" },
  invalid_qr: { icon: QrCode, label: "Invalid QR", color: "text-slate-600 bg-slate-50 border-slate-200" },
};

const fmtDT = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(() => { api.get("/alerts").then((r) => setAlerts(r.data)); }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (id) => { await api.post(`/alerts/${id}/read`); load(); };
  const markAll = async () => { await api.post("/alerts/read-all"); toast.success("All alerts marked read"); load(); };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <div className="space-y-6 fade-up" data-testid="alerts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1.5">Notifications</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Alerts</h1>
          <p className="mt-1 text-sm text-slate-500">{unread} unread · missing returns, wrong providers, duplicates & invalid scans</p>
        </div>
        {unread > 0 && (
          <Button variant="outline" onClick={markAll} data-testid="mark-all-read-button" className="rounded-full">
            <CheckCheck size={15} className="mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {alerts.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-soft py-14 text-center">
            <Bell size={28} className="mx-auto mb-3 text-slate-300" />
            <div className="text-sm text-slate-400">No alerts — everything looks good</div>
          </div>
        )}
        {alerts.map((a) => {
          const meta = TYPE_META[a.type] || { icon: AlertTriangle, label: a.type, color: "text-slate-600 bg-slate-50 border-slate-200" };
          return (
            <div key={a.id} data-testid={`alert-${a.type}`}
              className={`flex items-start justify-between gap-4 rounded-lg border bg-white p-4 ${a.read ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-3">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${meta.color}`}>
                  <meta.icon size={16} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{meta.label}</span>
                    {!a.read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                  </div>
                  <div className="mt-0.5 text-sm text-slate-800">{a.message}</div>
                  <div className="mt-1 font-mono text-xs text-slate-400">{fmtDT(a.created_at)}</div>
                </div>
              </div>
              {!a.read && (
                <button onClick={() => markRead(a.id)} data-testid={`mark-read-${a.id}`}
                  className="shrink-0 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500 transition-colors duration-200 hover:bg-slate-100">
                  Mark read
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
